import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../../lib/errors';
import { formatTimestamp } from '../../lib/format';
import type {
  AssistantActionCard,
  AssistantChange,
  AssistantMessageRead,
  ProposalStatus,
} from '../../types/api';
import { AssistantActionCards } from './AssistantActionCards';
import { AssistantChanges } from './AssistantChanges';
import { creditsUsedLabel } from './assistantCredits';
import { AssistantSources } from './AssistantSources';
import { applyProposal } from './applyProposal';
import { AssistantText } from './AssistantText';
import { resolveClientAction } from './clientActions';
import { ProposalCard } from './ProposalCard';

export type MarkProposal = (
  messageId: string,
  proposalIndex: number,
  status: ProposalStatus,
  resourceId?: string,
) => Promise<unknown>;

interface AssistantMessageProps {
  message: AssistantMessageRead;
  businessId: string;
  onMark: MarkProposal;
  /** Offered on the last failed reply only; re-sends the question it answered. */
  onRetry?: () => void;
  isRetryDisabled?: boolean;
  /** Undoes one of this reply's changes. */
  onUndo?: (messageId: string, actionId: string) => Promise<AssistantChange>;
  /** Sets off one of this reply's cards. */
  onConfirm?: (messageId: string, actionId: string) => Promise<AssistantActionCard>;
  /** Puts one of this reply's cards away. */
  onCancel?: (messageId: string, actionId: string) => Promise<AssistantActionCard>;
  /** Hands a sentence to the conversation's live region. */
  onAnnounce?: (text: string) => void;
}

function markFor(message: AssistantMessageRead, index: number) {
  return message.applied[String(index)] ?? null;
}

/** What the reply did in the browser, said in the past tense, with a way to do it again. */
function ClientActionNotes({ message }: { message: AssistantMessageRead }) {
  const navigate = useNavigate();
  if (message.client_actions.length === 0) return null;

  return (
    <ul className="assistant-msg__actions">
      {message.client_actions.map((action, index) => {
        const resolved = resolveClientAction(action);
        return (
          <li className="assistant-msg__action" key={index}>
            {resolved === null ? (
              <span>The assistant offered a page that cannot be opened from here.</span>
            ) : (
              <>
                <span>{resolved.done}</span>
                <button
                  className="assistant-msg__again"
                  type="button"
                  onClick={() => void navigate(resolved.to, { state: resolved.state })}
                >
                  Open again
                </button>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One turn. The user's words on the right as typed; the assistant's on the
 * left, drawn from light markdown, with the tools it ran, the edits it made
 * (each with Undo while offered), what it did in the browser, and any plan it
 * proposed as cards to apply or dismiss. A failed
 * reply is a stamp-red note, never half an answer.
 */
export function AssistantMessage({
  message,
  businessId,
  onMark,
  onRetry,
  isRetryDisabled = false,
  onUndo,
  onConfirm,
  onCancel,
  onAnnounce,
}: AssistantMessageProps) {
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [applyAllError, setApplyAllError] = useState<string | null>(null);

  const isUser = message.role === 'user';
  const hasFailed = message.status === 'failed';
  const isUpdate = !isUser && message.kind === 'followup';
  const undecided = message.proposals.filter(
    (_proposal, index) => markFor(message, index) === null,
  );

  /**
   * Applies what is left, in order. Sequential on purpose: each write is a real
   * record, and a failure halfway leaves the rest intact and still applicable.
   */
  async function applyAll() {
    setIsApplyingAll(true);
    setApplyAllError(null);
    try {
      for (const [index, proposal] of message.proposals.entries()) {
        if (markFor(message, index) !== null) continue;
        const resourceId = await applyProposal(businessId, proposal);
        await onMark(message.id, index, 'applied', resourceId);
      }
    } catch (caught) {
      setApplyAllError(
        getErrorMessage(caught, 'That stopped partway. What is left is still on the cards.'),
      );
    } finally {
      setIsApplyingAll(false);
    }
  }

  const rowClass = [
    'assistant-msg',
    isUser ? 'assistant-msg--user' : 'assistant-msg--assistant',
    hasFailed ? 'assistant-msg--failed' : '',
    isUpdate ? 'assistant-msg--update' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={rowClass}>
      {!isUser && message.steps.length > 0 ? (
        <ul className="assistant-steps assistant-steps--done" aria-label="What it checked">
          {message.steps.map((step, index) => (
            <li className="assistant-step" key={index}>
              {step.label}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="assistant-msg__bubble">
        {/* A turn the assistant took on its own, after a card it was following. */}
        {isUpdate ? <p className="assistant-msg__update">Update</p> : null}
        {hasFailed ? (
          <div className="assistant-msg__failure">
            <p className="assistant-msg__failure-text">The assistant did not finish this reply.</p>
            {onRetry ? (
              <button
                className="button button--ghost assistant-msg__retry"
                type="button"
                disabled={isRetryDisabled}
                onClick={onRetry}
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        {message.content ? (
          isUser ? (
            <p className="assistant-msg__user-text">{message.content}</p>
          ) : (
            <AssistantText content={message.content} />
          )
        ) : null}

        <p className="assistant-msg__stamp">
          {formatTimestamp(message.created_at)}
          {!isUser && message.credits_used > 0 ? (
            <span className="assistant-msg__credits">
              {' · '}
              {creditsUsedLabel(message.credits_used)}
            </span>
          ) : null}
        </p>
      </div>

      {!isUser ? <AssistantSources sources={message.sources} /> : null}

      {!isUser ? (
        <AssistantChanges
          changes={message.changes}
          onUndo={onUndo ? (actionId) => onUndo(message.id, actionId) : undefined}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {!isUser ? (
        <AssistantActionCards
          cards={message.actions}
          onConfirm={onConfirm ? (actionId) => onConfirm(message.id, actionId) : undefined}
          onCancel={onCancel ? (actionId) => onCancel(message.id, actionId) : undefined}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {!isUser ? <ClientActionNotes message={message} /> : null}

      {message.proposals.length > 0 ? (
        <div className="assistant-msg__proposals">
          {message.proposals.map((proposal, index) => (
            // The index is the identity: the server keys `applied` by it.
            <ProposalCard
              key={index}
              proposal={proposal}
              businessId={businessId}
              mark={markFor(message, index)}
              isLocked={isApplyingAll}
              onMark={(status, resourceId) => onMark(message.id, index, status, resourceId)}
            />
          ))}

          {undecided.length > 1 ? (
            <button
              className="button button--ghost assistant-msg__apply-all"
              type="button"
              disabled={isApplyingAll}
              onClick={() => void applyAll()}
            >
              {isApplyingAll ? 'Applying all' : `Apply all ${undecided.length}`}
            </button>
          ) : null}

          {applyAllError ? (
            <p className="form__error" role="alert">
              {applyAllError}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
